import { useState, useRef, useEffect } from "react";
import { DayPicker } from "react-day-picker";
import { format, isAfter, isBefore, startOfDay } from "date-fns";
import styles from "./Calendar.module.css";
import "react-day-picker/style.css";
import Icon from "../Icon/Icon";
import { es } from "react-day-picker/locale";

type CalendarPropsType = {
  selectedDate: Date | undefined;
  setSelectedDate: (arg: Date | undefined) => void;
  minDate?: Date;
  maxDate?: Date;
  wraperClassName?: string;
  pickerClassName?: string;
};

const Calendar = ({
  selectedDate,
  setSelectedDate,
  minDate,
  maxDate,
  wraperClassName,
  pickerClassName,
}: CalendarPropsType) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDayClick = (day: Date) => {
    const clicked = startOfDay(day);
    const current = selectedDate ? startOfDay(selectedDate) : undefined;
    const today = startOfDay(new Date());
    const min = minDate ? startOfDay(minDate) : undefined;
    const max = maxDate ? startOfDay(maxDate) : undefined;

    if (isAfter(clicked, today)) return;
    if (min && isBefore(clicked, min)) return;
    if (max && isAfter(clicked, max)) return;

    if (current && clicked.getTime() === current.getTime()) {
      setSelectedDate(undefined); // unselect
    } else {
      setSelectedDate(clicked); // select
    }

    setOpen(false);
  };

  return (
    <div className={`${styles.wrapper} ${wraperClassName || ""}`} ref={ref}>
      <button
        type="button"
        className={styles.datePickerInput}
        onClick={() => setOpen((prev) => !prev)}
      >
        <Icon name="calendar" size={20} />
        {selectedDate ? format(selectedDate, "dd/MM/yyyy") : "DD/MM/YY"}
      </button>

      {open && (
        <div className={`${styles.picker} ${pickerClassName || ""}`}>
          <DayPicker
            mode="single"
            selected={selectedDate}
            onDayClick={handleDayClick}
            locale={es}
            showOutsideDays
            navLayout="after"
            disabled={[
              { after: new Date() },
              ...(maxDate ? [{ after: maxDate }] : []),
              ...(minDate ? [{ before: minDate }] : []),
            ]}
          />
        </div>
      )}
    </div>
  );
};

export default Calendar;
